import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, addHours, isFuture } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Wrench, Pencil, Trash2, Clock, FileText, Truck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { WorkOrder, Tractor, Location } from "@/types/tms";

import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { WorkOrderCard } from "@/components/maintenance/WorkOrderCard";

interface WorkOrderWithTractor extends WorkOrder {
  tractor: (Tractor & { home_base: Location | null }) | null;
}

type FilterStatus = "all" | "active" | "upcoming" | "completed";

const maintenanceTabs = [
  { value: "internal", label: "Internal Work Orders", icon: Wrench },
] as const;

export default function Maintenance() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>("internal");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingWorkOrder, setEditingWorkOrder] = useState<WorkOrderWithTractor | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Edit form state
  const [editStartTime, setEditStartTime] = useState("");
  const [editDurationHours, setEditDurationHours] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const { data: workOrders, isLoading } = useQuery({
    queryKey: ["all-work-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select(`
          *,
          tractor:tractors(*, home_base:locations(*))
        `)
        .order("start_time", { ascending: false });
      if (error) throw error;
      return data as WorkOrderWithTractor[];
    },
  });

  const updateWorkOrder = useMutation({
    mutationFn: async ({
      id,
      startTime,
      durationHours,
      description,
    }: {
      id: string;
      startTime: string;
      durationHours: number;
      description: string | null;
    }) => {
      const startDate = new Date(startTime);
      const endDate = addHours(startDate, durationHours);

      // Update the work order
      const { error } = await supabase
        .from("work_orders")
        .update({
          start_time: startDate.toISOString(),
          duration_hours: durationHours,
          description,
        })
        .eq("id", id);

      if (error) throw error;

      // If time changed, check for and unassign overlapping orders
      const workOrder = workOrders?.find((wo) => wo.id === id);
      if (workOrder) {
        const { data: overlappingOrders, error: fetchError } = await supabase
          .from("orders")
          .select("id, unique_id")
          .eq("assigned_tractor_id", workOrder.tractor_id)
          .lt("ready_time", endDate.toISOString())
          .gt("requested_delivery_time", startDate.toISOString());

        if (fetchError) throw fetchError;

        if (overlappingOrders && overlappingOrders.length > 0) {
          // Batch unassign overlapping orders
          const { error: unassignError } = await supabase
            .from("orders")
            .update({ assigned_tractor_id: null })
            .in("id", overlappingOrders.map((o) => o.id));

          if (unassignError) throw unassignError;

          // Batch log the unassignments
          const logEntries = overlappingOrders.map((order) => ({
            order_id: order.id,
            field_name: "assigned_tractor_id",
            old_value: workOrder.tractor?.name || null,
            new_value: null,
            changed_by: "System (Work Order Update)",
          }));

          const { error: logError } = await supabase
            .from("order_change_log")
            .insert(logEntries);

          if (logError) console.error("Failed to log changes:", logError);

          return { unassignedCount: overlappingOrders.length };
        }
      }

      return { unassignedCount: 0 };
    },
    onSuccess: ({ unassignedCount }) => {
      queryClient.invalidateQueries({ queryKey: ["all-work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });

      let message = "Work order updated successfully";
      if (unassignedCount > 0) {
        message += `. ${unassignedCount} overlapping order(s) had tractor unassigned.`;
      }
      toast.success(message);

      setIsEditDialogOpen(false);
      setEditingWorkOrder(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update work order: ${error.message}`);
    },
  });

  const deleteWorkOrder = useMutation({
    mutationFn: async (id: string) => {
      // Find the work order to get tractor_id
      const workOrder = workOrders?.find((wo) => wo.id === id);
      
      // Delete from work_orders table
      const { error } = await supabase.from("work_orders").delete().eq("id", id);
      if (error) throw error;

      // Remove from tractor's work_order_ids array
      if (workOrder?.tractor_id) {
        const { data: tractor } = await supabase
          .from("tractors")
          .select("work_order_ids")
          .eq("id", workOrder.tractor_id)
          .maybeSingle();

        if (tractor) {
          const updatedWorkOrderIds = (tractor.work_order_ids || []).filter(
            (woId) => woId !== id
          );
          await supabase
            .from("tractors")
            .update({ work_order_ids: updatedWorkOrderIds })
            .eq("id", workOrder.tractor_id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["tractors-with-home-base"] });
      toast.success("Work order deleted");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete work order: ${error.message}`);
    },
  });

  const handleEdit = (workOrder: WorkOrderWithTractor) => {
    setEditingWorkOrder(workOrder);
    setEditStartTime(format(new Date(workOrder.start_time), "yyyy-MM-dd'T'HH:mm"));
    setEditDurationHours(String(workOrder.duration_hours));
    setEditDescription(workOrder.description || "");
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingWorkOrder) return;

    updateWorkOrder.mutate({
      id: editingWorkOrder.id,
      startTime: editStartTime,
      durationHours: parseFloat(editDurationHours) || 4,
      description: editDescription || null,
    });
  };

  const getWorkOrderStatus = (workOrder: WorkOrder): "active" | "upcoming" | "completed" => {
    const startTime = new Date(workOrder.start_time);
    const endTime = addHours(startTime, workOrder.duration_hours);
    const now = new Date();

    if (now >= startTime && now <= endTime) return "active";
    if (isFuture(startTime)) return "upcoming";
    return "completed";
  };

  const getStatusBadge = (status: "active" | "upcoming" | "completed") => {
    switch (status) {
      case "active":
        return <Badge className="bg-[hsl(var(--status-in-transit))] text-white">Active</Badge>;
      case "upcoming":
        return <Badge variant="outline" className="border-[hsl(var(--status-confirmed))] text-[hsl(var(--status-confirmed))]">Upcoming</Badge>;
      case "completed":
        return <Badge variant="secondary">Completed</Badge>;
    }
  };

  const filteredWorkOrders = workOrders?.filter((wo) => {
    // Status filter
    if (filterStatus !== "all") {
      const status = getWorkOrderStatus(wo);
      if (status !== filterStatus) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTractor = wo.tractor?.name.toLowerCase().includes(query);
      const matchesId = wo.unique_id.toLowerCase().includes(query);
      const matchesDescription = wo.description?.toLowerCase().includes(query);
      if (!matchesTractor && !matchesId && !matchesDescription) return false;
    }

    return true;
  });


  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows: pagedWorkOrders,
  } = useTablePagination(filteredWorkOrders);

  const counts = {
    all: workOrders?.length || 0,
    active: workOrders?.filter((wo) => getWorkOrderStatus(wo) === "active").length || 0,
    upcoming: workOrders?.filter((wo) => getWorkOrderStatus(wo) === "upcoming").length || 0,
    completed: workOrders?.filter((wo) => getWorkOrderStatus(wo) === "completed").length || 0,
  };

  return (
    <AppLayout
      title="Maintenance"
      subtitle={
        <div className="flex items-center gap-6">
          {maintenanceTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                "flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === tab.value
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.icon && <tab.icon className="h-4 w-4" />}
              {tab.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-4 animate-fade-in">
        {activeTab === "internal" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex gap-2">
                {(["all", "active", "upcoming", "completed"] as FilterStatus[]).map((status) => (
                  <Button
                    key={status}
                    variant={filterStatus === status ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterStatus(status)}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)} ({counts[status]})
                  </Button>
                ))}
              </div>
              <SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />
              <div className="ml-auto flex items-center gap-3">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  Showing {filteredWorkOrders?.length ?? 0} work order{(filteredWorkOrders?.length ?? 0) === 1 ? "" : "s"}
                </span>
                <ExpandingSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search by tractor, ID, or description..."
                />
              </div>
            </div>

            {/* Work Orders Table */}
            <div className="border rounded-lg">
              <ResponsiveTableShell
                cards={
                  <div className="flex flex-col gap-3 p-3">
                    {isLoading ? (
                      <div className="text-center py-8 text-muted-foreground">Loading...</div>
                    ) : pagedWorkOrders.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        {(searchQuery.trim() || filterStatus !== "all")
                          ? "No work orders match your filter criteria"
                          : "No work orders found"}
                      </div>
                    ) : (
                      pagedWorkOrders.map((wo) => (
                        <WorkOrderCard
                          key={wo.id}
                          workOrder={wo}
                          statusBadge={getStatusBadge(getWorkOrderStatus(wo))}
                          onEdit={handleEdit}
                          onDelete={(id) => deleteWorkOrder.mutate(id)}
                        />
                      ))
                    )}
                  </div>
                }
                table={
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Tractor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Expected Back</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : pagedWorkOrders.length === 0 ? (
                    <TableRow>
                       <TableCell colSpan={8} className="text-center text-muted-foreground">
                         {(searchQuery.trim() || filterStatus !== "all") ? "No work orders match your filter criteria" : "No work orders found"}
                       </TableCell>
                    </TableRow>
                  ) : (
                    pagedWorkOrders.map((wo) => {
                      const status = getWorkOrderStatus(wo);
                      const startTime = new Date(wo.start_time);
                      const endTime = addHours(startTime, wo.duration_hours);

                      return (
                        <TableRow key={wo.id}>
                          <TableCell className="font-mono text-sm">{wo.unique_id}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Truck className="h-4 w-4 text-muted-foreground" />
                              {wo.tractor?.name || "Unknown"}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(status)}</TableCell>
                          <TableCell>
                            <div>
                              <div>{format(startTime, "MMM d, yyyy")}</div>
                              <div className="text-xs text-muted-foreground">
                                {format(startTime, "h:mm a")}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{wo.duration_hours}h</TableCell>
                          <TableCell>
                            <div>
                              <div>{format(endTime, "MMM d, yyyy")}</div>
                              <div className="text-xs text-muted-foreground">
                                {format(endTime, "h:mm a")}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {wo.description || <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(wo)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (confirm("Delete this work order?")) {
                                    deleteWorkOrder.mutate(wo.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
                }
              />
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </>
        )}

      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-[hsl(var(--status-in-transit))]" />
              Edit Work Order
            </DialogTitle>
          </DialogHeader>

          {editingWorkOrder && (
            <div className="space-y-4 py-4">
              {/* Work Order Info */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-sm font-medium">{editingWorkOrder.unique_id}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Tractor: {editingWorkOrder.tractor?.name || "Unknown"}
                </div>
              </div>

              {/* Start Time */}
              <div className="space-y-2">
                <Label htmlFor="edit-start-time" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Start Time
                </Label>
                <Input
                  id="edit-start-time"
                  type="datetime-local"
                  value={editStartTime}
                  onChange={(e) => setEditStartTime(e.target.value)}
                />
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label htmlFor="edit-duration">Duration (hours)</Label>
                <Input
                  id="edit-duration"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={editDurationHours}
                  onChange={(e) => setEditDurationHours(e.target.value)}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="edit-description" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Description
                </Label>
                <Textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="e.g., Oil change, brake inspection, tire rotation..."
                  rows={3}
                />
              </div>

              {/* Warning */}
              <div className="text-xs text-muted-foreground bg-[hsl(var(--status-in-transit)/0.1)] p-3 rounded-lg">
                <strong>Note:</strong> Changing the time may unassign overlapping orders from this tractor.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateWorkOrder.isPending}
              className="bg-[hsl(var(--status-in-transit))] hover:bg-[hsl(var(--status-in-transit)/0.9)]"
            >
              {updateWorkOrder.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
